import { useEffect, useMemo, useState } from 'react';
import { UserRepository } from '../../connectors/repositories/user';
import { normalizeAvatar } from '../avatar';

const profileCache = new Map();

const normalizeAddresses = (addresses) => {
  return [...new Set((addresses || []).filter(Boolean).map((address) => String(address).toLowerCase()))];
};

export const useUserProfiles = (addresses = []) => {
  const keys = useMemo(() => normalizeAddresses(addresses), [addresses]);
  const keysKey = keys.join(',');
  const [profiles, setProfiles] = useState({});

  useEffect(() => {
    const unique = keysKey ? keysKey.split(',') : [];

    if (!unique.length) {
      setProfiles({});
      return undefined;
    }

    const missing = unique.filter((address) => !profileCache.has(address));

    setProfiles((prev) => {
      const next = { ...prev };
      unique.forEach((address) => {
        if (profileCache.has(address)) {
          next[address] = profileCache.get(address);
        }
      });
      return next;
    });

    if (!missing.length) {
      return undefined;
    }

    let cancelled = false;

    Promise.all(
      missing.map(async (address) => {
        try {
          const result = await UserRepository.getProfileWithoutToken(address);
          const profile = {
            avatar: normalizeAvatar(result?.imageUrl),
            nickname: result?.nickname || null,
          };
          profileCache.set(address, profile);
          return [address, profile];
        } catch {
          const profile = { avatar: null, nickname: null };
          profileCache.set(address, profile);
          return [address, profile];
        }
      }),
    ).then((entries) => {
      if (cancelled) {
        return;
      }

      setProfiles((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    });

    return () => {
      cancelled = true;
    };
  }, [keysKey]);

  return profiles;
};
